import { useQuery } from "@tanstack/react-query";
import { ShipTier } from "@voidex/contracts-ts";
import { querySubgraph } from "../web3/subgraph";
import { FarmOutcome } from "./usePreTGE";

export interface ResolvedMission {
	sessionId: bigint;
	shipTokenId: bigint;
	shipTier: ShipTier;
	outcome: FarmOutcome;
	pointsEarned: bigint;
	shipDestroyed: boolean;
	startedAtBlock: bigint;
	resolvedAtBlock: bigint;
	resolvedTimestamp: bigint;
}

interface SubgraphResolvedSession {
	id: string;
	shipTokenId: string;
	ship: { tier: "Probe" | "Frigate" | "Dreadnought" };
	outcome: string;
	pointsEarned: string | null;
	shipDestroyed: boolean;
	startedAt: string;
	resolvedAt: string | null;
	resolvedTimestamp: string | null;
}

interface ResolvedSessionQueryResult {
	farmSessions: SubgraphResolvedSession[];
}

const tierMap: Record<string, ShipTier> = {
	Probe: ShipTier.Probe,
	Frigate: ShipTier.Frigate,
	Dreadnought: ShipTier.Dreadnought,
};

const outcomeMap: Record<string, FarmOutcome> = {
	Destroyed: FarmOutcome.Destroyed,
	Poor: FarmOutcome.Poor,
	Standard: FarmOutcome.Standard,
	Rich: FarmOutcome.Rich,
	Jackpot: FarmOutcome.Jackpot,
};

/**
 * Fetches resolved (completed) farm sessions for `address` from the subgraph.
 */
export function useMissionHistory(address?: `0x${string}`) {
	const { data, isLoading, error, refetch } = useQuery({
		queryKey: ["missionHistory", address],
		queryFn: async (): Promise<ResolvedMission[]> => {
			if (!address) return [];

			const farmerId = address.toLowerCase();
			const result = await querySubgraph<ResolvedSessionQueryResult>(
				`query ResolvedSessions($farmer: String!) {
					farmSessions(
						where: { farmer: $farmer, resolved: true }
						orderBy: resolvedAt
						orderDirection: desc
						first: 50
					) {
						id
						shipTokenId
						ship { tier }
						outcome
						pointsEarned
						shipDestroyed
						startedAt
						resolvedAt
						resolvedTimestamp
					}
				}`,
				{ farmer: farmerId },
			);

			return result.farmSessions.map((s) => ({
				sessionId: BigInt(s.id),
				shipTokenId: BigInt(s.shipTokenId),
				shipTier: tierMap[s.ship.tier] ?? ShipTier.Probe,
				outcome: outcomeMap[s.outcome] ?? FarmOutcome.Destroyed,
				pointsEarned: BigInt(s.pointsEarned ?? "0"),
				shipDestroyed: s.shipDestroyed,
				startedAtBlock: BigInt(s.startedAt),
				resolvedAtBlock: BigInt(s.resolvedAt ?? "0"),
				resolvedTimestamp: BigInt(s.resolvedTimestamp ?? "0"),
			}));
		},
		enabled: !!address,
		staleTime: 15_000,
		refetchInterval: 30_000,
	});

	return {
		missions: data ?? [],
		isLoading,
		error,
		refetch,
	};
}
